import { ActionTree } from "vuex";
import { IRootState } from "@/store/types";
import { UI } from "@/store/ui";
import { STANDARD_OFFER, state as defaultState } from ".";
import { IStandardOfferState } from "./types";
import { STANDARD_OFFER_MUTATIONS } from "./mutations";
import api from "@/api/signup/api";
import { cloneDeep } from "@/helpers/object";
import { ERROR_MESSAGES } from "@/constants/errors";

export namespace STANDARD_OFFER_ACTIONS {
  export const RESET_STATE = "resetState";
  export const LOAD_STANDARD_OFFERS_LIST = "loadOffersList";
  export const LOAD_STANDARD_OFFER = "loadOffer";
  export const LOAD_STANDARD_OFFER_BY_OFFER_ID = "loadOfferByOfferId";
  export const CREATE_STANDARD_OFFER = "createOffer";
  export const UPDATE_STANDARD_OFFER = "updateOffer";
}

export const actions: ActionTree<IStandardOfferState, IRootState> = {
  [STANDARD_OFFER_ACTIONS.RESET_STATE]({ commit }): void {
    commit(STANDARD_OFFER_MUTATIONS.SET_OFFER, cloneDeep(defaultState.offer));
  },
  async [STANDARD_OFFER_ACTIONS.LOAD_STANDARD_OFFERS_LIST]({
    commit,
    dispatch
  }): Promise<void> {
    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      const response: any = await api.getStandardOffers();
      commit(STANDARD_OFFER.MUTATIONS.SET_OFFERS_LIST, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-standard-offer-list",
          text: ERROR_MESSAGES.LOAD_STANDARD_OFFERS_LIST_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [STANDARD_OFFER_ACTIONS.LOAD_STANDARD_OFFER](
    { commit, dispatch },
    id: number
  ): Promise<void> {
    try {
      const response: any = await api.getStandardOffer(id);
      // merge with defaultState incase there are field not returned by the API otherwise
      // the reactivity won't work properly
      const offer = Object.assign(cloneDeep(defaultState.offer), response.data);

      commit(STANDARD_OFFER.MUTATIONS.SET_OFFER, offer);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-standard-offer",
          text: ERROR_MESSAGES.LOAD_STANDARD_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }
  },
  async [STANDARD_OFFER_ACTIONS.LOAD_STANDARD_OFFER_BY_OFFER_ID](
    { commit, dispatch },
    id: number
  ): Promise<void> {
    try {
      const response: any = await api.getStandardOfferByOfferId(id);
      // merge with defaultState incase there are field not returned by the API otherwise
      // the reactivity won't work properly
      const offer = Object.assign(cloneDeep(defaultState.offer), response.data);

      commit(STANDARD_OFFER.MUTATIONS.SET_OFFER, offer);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-standard-offer",
          text: ERROR_MESSAGES.LOAD_STANDARD_OFFER_BY_OFFER_ID_ERROR,
          error
        },
        { root: true }
      );
    }
  },
  async [STANDARD_OFFER_ACTIONS.CREATE_STANDARD_OFFER](
    { commit, dispatch, state },
    onSuccess: () => any
  ): Promise<void> {
    if (!state.offer) {
      return;
    }

    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      await api.createStandardOffer(state.offer);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "created-standard-offer",
          text: "Successfully created standard offer"
        },
        { root: true }
      );

      onSuccess();
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-submit-standard-offer",
          text: ERROR_MESSAGES.CREATE_STANDARD_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [STANDARD_OFFER_ACTIONS.UPDATE_STANDARD_OFFER](
    { commit, dispatch, state },
    id: number
  ): Promise<void> {
    if (!state.offer) {
      return;
    }

    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      await api.updateStandardOffer(state.offer, id);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "created-standard-offer",
          text: "Successfully updated standard offer"
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-save-standard-offer",
          text: ERROR_MESSAGES.UPDATE_STANDARD_OFFER_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(STANDARD_OFFER.MUTATIONS.TOGGLE_IS_LOADING, false);
  }
};
